// Country-name variant generation.
//
// Many countries have long official forms ("Plurinational State of Bolivia",
// "Bosnia and Herzegovina") but users naturally type the short form
// ("Bolivia", "Bosnia"). This module derives those short forms from the
// official names so the fuzzy matcher accepts them, without us having to
// hand-curate every alias.
//
// Two rules:
//   1. Strip a leading qualifying phrase like "Republic of", "Kingdom of",
//      "Plurinational State of", etc.
//   2. If the (possibly-stripped) name contains " and ", also accept the
//      part before " and " on its own — e.g. "Bosnia and Herzegovina" →
//      "Bosnia".
//
// We intentionally keep this conservative: we never tokenize on whitespace
// arbitrarily (which would produce "Central" and "African" as variants of
// "Central African Republic"), only on the specific " and " conjunction.
// Curated aliases in data/aliases.ts handle the few cases this misses.

const LEADING_PHRASES: RegExp[] = [
  /^the\s+/i,
  /^republic of the\s+/i,
  /^republic of\s+/i,
  /^kingdom of\s+/i,
  /^plurinational state of\s+/i,
  /^islamic republic of\s+/i,
  /^democratic people'?s republic of\s+/i,
  /^democratic republic of the\s+/i,
  /^democratic republic of\s+/i,
  /^federated states of\s+/i,
  /^federal republic of\s+/i,
  /^united republic of\s+/i,
  /^socialist republic of\s+/i,
  /^people'?s republic of\s+/i,
  /^co-?operative republic of\s+/i,
  /^commonwealth of\s+/i,
  /^state of\s+/i,
  /^sultanate of\s+/i,
  /^principality of\s+/i,
  /^grand duchy of\s+/i,
  /^bolivarian republic of\s+/i,
  /^federation of\s+/i,
  /^union of\s+/i,
  /^independent state of\s+/i,
];

const stripLeadingPhrase = (name: string): string => {
  let result = name;
  // Iterate — "Republic of the Congo" needs two passes if we have both
  // "the" and "republic of" rules. Cheap: bound it.
  for (let i = 0; i < 3; i++) {
    let progressed = false;
    for (const re of LEADING_PHRASES) {
      const next = result.replace(re, "");
      if (next !== result) {
        result = next;
        progressed = true;
        break;
      }
    }
    if (!progressed) break;
  }
  return result;
};

// Splits a name like "Trinidad and Tobago" or
// "Saint Vincent and the Grenadines" into its parts. The split is on the
// FIRST " and " only — names with multiple " and "s (rare) are treated as
// two-part anyway, since further splitting tends to produce nonsense.
const splitOnAnd = (name: string): string[] => {
  const match = / and /i.exec(name);
  if (!match) return [];
  const before = name.slice(0, match.index).trim();
  const after = name.slice(match.index + 5).trim();
  return [before, after].filter((p) => p.length >= 3);
};

// Returns the input plus any derived short forms. Always includes the
// original name verbatim so the caller can pass the result directly to the
// fuzzy matcher.
export const expandNameVariants = (name: string): string[] => {
  const out = new Set<string>();
  const trimmed = name.trim();
  if (trimmed.length === 0) return [];
  out.add(trimmed);

  const stripped = stripLeadingPhrase(trimmed);
  if (stripped !== trimmed && stripped.length >= 3) out.add(stripped);

  for (const part of splitOnAnd(stripped)) out.add(part);
  // Also derive from the original, so names like
  // "Federation of Saint Kitts and Nevis" still yield "Saint Kitts" and
  // "Nevis" even though our prefix-strip rules don't include "Federation
  // of <Title>".
  for (const part of splitOnAnd(trimmed)) out.add(part);

  return [...out];
};
